import type { ReactElement, ReactNode } from 'react'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * Page title with optional always-visible meta (counts, venue name) and optional
 * explanatory copy on hover — keeps list headers quiet after the first visit
 * without losing the hint for newcomers.
 */
export function PageHeader({
  title,
  description,
  meta,
  className,
}: {
  title: string
  /** Shown on hover of the title (explanatory blurb). */
  description?: string
  /** Always-visible secondary line (e.g. "12 total", a venue name). */
  meta?: ReactNode
  className?: string
}): ReactElement {
  const heading = (
    <h1
      className={cn(
        'text-title font-semibold text-ink [text-wrap:balance]',
        description ? 'w-fit max-w-full cursor-default' : undefined,
      )}
    >
      {title}
    </h1>
  )

  return (
    <div className={cn(className)}>
      {description ? (
        <Tooltip content={description} side="bottom" align="start">
          {heading}
        </Tooltip>
      ) : (
        heading
      )}
      {meta != null && meta !== false && meta !== '' ? (
        <p className="mt-1 min-w-0 max-w-[62ch] wrap-anywhere text-sm text-ink-muted">{meta}</p>
      ) : null}
    </div>
  )
}

/**
 * Section heading (h2) with optional description as hover tooltip.
 * For in-page blocks (Settings tracks, Profile appearance, …).
 */
export function SectionHeader({
  title,
  description,
  icon,
  className,
}: {
  title: string
  description?: string
  icon?: ReactNode
  className?: string
}): ReactElement {
  const label = (
    <h2
      className={cn(
        'flex items-center gap-2 text-heading font-semibold text-ink',
        description ? 'w-fit max-w-full cursor-default' : undefined,
      )}
    >
      {icon}
      {title}
    </h2>
  )

  return (
    <div className={cn(className)}>
      {description ? (
        <Tooltip content={description} side="bottom" align="start">
          {label}
        </Tooltip>
      ) : (
        label
      )}
    </div>
  )
}
